import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Receipt } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ExpenseCard } from "../components/expenses/ExpenseCard";
import { EmptyState } from "../components/shared/EmptyState";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { SwipeableCard } from "../components/shared/SwipeableCard";
import { useEvents } from "../context/EventContext";
import { formatCurrency } from "../lib/currency";

export function ExpensesTab() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { currentEvent, deleteExpense } = useEvents();
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);

  if (!currentEvent) return null;

  const { participants, expenses, currency } = currentEvent;

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  // Sort expenses by most recent first
  const sortedExpenses = [...expenses].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleEditExpense = (expenseId: string) => {
    navigate(`/event/${eventId}/expenses/${expenseId}/edit`);
  };

  const handleDeleteExpense = (expenseId: string) => {
    setExpenseToDelete(expenseId);
  };

  const handleSwipeDelete = (expenseId: string) => {
    // Direct delete for swipe (undo available via toast)
    deleteExpense(expenseId);
  };

  const confirmDeleteExpense = () => {
    if (expenseToDelete) {
      deleteExpense(expenseToDelete);
      setExpenseToDelete(null);
    }
  };

  const canAddExpense = participants.length > 0;

  return (
    <>
      <div className="p-4 pb-20 space-y-4">
        {/* Summary */}
        {expenses.length > 0 && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Total gastado
                </span>
                <span className="text-lg font-semibold">
                  {formatCurrency(totalExpenses, currency)}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No participants warning */}
        {!canAddExpense && (
          <Card>
            <CardContent className="py-6">
              <EmptyState
                icon={<Receipt className="h-8 w-8" />}
                title="Agrega participantes primero"
                description="Ve a la pestaña de Grupo para agregar personas"
              />
            </CardContent>
          </Card>
        )}

        {/* Expense list */}
        {sortedExpenses.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              Gastos ({expenses.length})
            </h2>
            {sortedExpenses.map((expense) => (
              <SwipeableCard
                key={expense.id}
                onDelete={() => handleSwipeDelete(expense.id)}
              >
                <ExpenseCard
                  expense={expense}
                  participants={participants}
                  currency={currency}
                  onEdit={() => handleEditExpense(expense.id)}
                  onDelete={() => handleDeleteExpense(expense.id)}
                />
              </SwipeableCard>
            ))}
          </div>
        ) : (
          canAddExpense && (
            <Card>
              <CardContent className="py-6">
                <EmptyState
                  icon={<Receipt className="h-12 w-12" />}
                  title="Sin gastos"
                  description="Agrega el primer gasto del evento"
                />
              </CardContent>
            </Card>
          )
        )}
      </div>

      {/* Floating add expense button */}
      {canAddExpense && (
        <Button
          size="lg"
          className="fixed right-4 bottom-20 h-14 w-14 rounded-full shadow-lg mb-[env(safe-area-inset-bottom)] z-10"
          onClick={() => navigate(`/event/${eventId}/expenses/new`)}
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      <ConfirmDialog
        open={expenseToDelete !== null}
        onOpenChange={(open) => !open && setExpenseToDelete(null)}
        title="Eliminar gasto"
        description="¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={confirmDeleteExpense}
      />
    </>
  );
}
